"use client";

import { Clock, Users } from "lucide-react";
import { parseLocalDateKey } from "@/lib/dates/local-date";
import { cn, formatTimeRange } from "@/lib/utils";
import type { NormalizedOccurrence } from "./event-page.shared";

interface SlotSelectorsProps {
  showSlotSelectors: boolean;
  dateKeys: string[];
  selectedDateKey: string | null;
  handleDateChange: (date: string) => void;
  occurrencesForSelectedDate: NormalizedOccurrence[];
  eventPattern: string;
  selectedOccurrence: NormalizedOccurrence | null;
  handleSlotChange: (slotId: string) => void;
  /** Attendees tab: each time chip carries its own headcount. */
  showAttendeeCounts?: boolean;
}

export const SlotSelectors = ({
  showSlotSelectors,
  dateKeys,
  selectedDateKey,
  handleDateChange,
  occurrencesForSelectedDate,
  eventPattern,
  selectedOccurrence,
  handleSlotChange,
  showAttendeeCounts = false,
}: SlotSelectorsProps) => {
  if (!showSlotSelectors) return null;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-surface px-3 py-3">
      {dateKeys.length > 1 ? (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium tracking-wider text-text-secondary uppercase">
            Select Date
          </span>
          <div className="scrollbar-hide flex gap-2 overflow-x-auto pb-2">
            {dateKeys.map((date) => {
              const isSelected = date === selectedDateKey;
              const dateObj = parseLocalDateKey(date);
              if (!dateObj) return null;
              return (
                <button
                  type="button"
                  key={date}
                  aria-pressed={isSelected}
                  onClick={() => handleDateChange(date)}
                  className={cn(
                    "flex min-w-[80px] flex-col items-center rounded-lg border px-3 py-2 transition-colors focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97]",
                    isSelected
                      ? "border-brand bg-brand text-brand-foreground hover:bg-brand-hover dark:border-white dark:bg-white dark:text-black dark:hover:bg-white/92"
                      : "border-border bg-surface-alt text-text-primary hover:bg-surface-elevated",
                  )}
                >
                  <span className="text-lg font-bold">{dateObj.getDate()}</span>
                  <span className="text-xs uppercase">
                    {dateObj.toLocaleDateString("en-US", {
                      month: "short",
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {occurrencesForSelectedDate.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium tracking-wider text-text-secondary uppercase">
            Select Time
          </span>
          <div className="flex flex-wrap gap-2">
            {eventPattern === "multi_date_single_slot" ? (
              <div className="flex w-full items-center gap-4 rounded-xl border border-surface-alt bg-surface p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {(() => {
                      const occurrence = occurrencesForSelectedDate[0];
                      const timeRange = formatTimeRange(
                        occurrence.start,
                        occurrence.end,
                      );
                      return occurrence.slotLabel
                        ? occurrence.slotLabel
                        : timeRange;
                    })()}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {(() => {
                      const occurrence = occurrencesForSelectedDate[0];
                      const timeRange = formatTimeRange(
                        occurrence.start,
                        occurrence.end,
                      );
                      return occurrence.slotLabel
                        ? timeRange
                        : (occurrence.timezone ?? "Local event time");
                    })()}
                  </p>
                </div>
              </div>
            ) : eventPattern === "multi_date" ? (
              <div className="flex items-center gap-2 rounded-full border border-surface-alt bg-surface px-4 py-2 text-sm font-medium text-text-secondary">
                <Clock className="h-4 w-4" />
                {(() => {
                  const occurrence = occurrencesForSelectedDate[0];
                  const timeRange = formatTimeRange(
                    occurrence.start,
                    occurrence.end,
                  );
                  return occurrence.slotLabel
                    ? `${occurrence.slotLabel} • ${timeRange}`
                    : timeRange;
                })()}
              </div>
            ) : (
              occurrencesForSelectedDate.map((occurrence) => {
                const isSelected = selectedOccurrence?.id === occurrence.id;
                const isClosed = occurrence.status !== "scheduled";
                const closedLabel =
                  occurrence.status === "completed"
                    ? "Ended"
                    : occurrence.status === "canceled"
                      ? "Canceled"
                      : "Closed";
                return (
                  <button
                    type="button"
                    key={occurrence.id}
                    aria-pressed={isSelected}
                    onClick={() => handleSlotChange(occurrence.id.toString())}
                    className={cn(
                      "inline-flex items-center rounded-full border px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97]",
                      isSelected
                        ? "border-brand bg-brand text-brand-foreground hover:bg-brand-hover dark:border-white dark:bg-white dark:text-black dark:hover:bg-white/92"
                        : "border-border bg-surface-alt text-text-primary hover:bg-surface-elevated",
                      isClosed && "opacity-60",
                    )}
                  >
                    {formatTimeRange(occurrence.start, occurrence.end)}
                    {isClosed ? ` • ${closedLabel}` : ""}
                    {showAttendeeCounts ? (
                      <span className="ml-2 inline-flex items-center gap-1 text-[0.85em] tabular-nums opacity-70">
                        <Users className="size-3.5" aria-hidden />
                        {occurrence.attendeeCount}
                        <span className="sr-only">
                          {occurrence.attendeeCount === 1
                            ? "attendee"
                            : "attendees"}
                        </span>
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
